import * as v from 'valibot';

/**
 * The maintenance loop: the fleet-wide changes nobody volunteers for.
 *
 * A framework upgrade across four hundred repositories is not four hundred
 * decisions. It is one decision and four hundred applications of it, and the
 * only reason it usually takes a quarter is that the applying is done by people
 * who each have to be persuaded. An agent applies it, and then the problem
 * inverts: four hundred pull requests landing on a Monday morning is not a
 * migration, it is an outage of the review process.
 *
 * So a campaign carries four constraints that matter more than the agent does:
 *
 *   1. **One enrollment surface.** A team opts a repository in once, centrally,
 *      rather than being chased per migration.
 *   2. **A window.** Runs are scheduled when CI is idle — Sunday — because a
 *      migration competing with people's own builds is a migration that gets
 *      switched off by whoever owns CI.
 *   3. **A cap.** A hard ceiling on diffs that land per run, so Monday's review
 *      queue is a number somebody chose.
 *   4. **Feedback.** What failed and what landed comes back as evidence for the
 *      skill that drove it, which is how the next campaign is better rather
 *      than merely later.
 */

export const MaintenanceKind = v.picklist([
  'migration',
  'dependency',
  'lint',
  'security',
  'cleanup',
]);
export type MaintenanceKind = v.InferOutput<typeof MaintenanceKind>;

export const MaintenanceState = v.picklist(['draft', 'active', 'paused', 'complete']);
export type MaintenanceState = v.InferOutput<typeof MaintenanceState>;

export const MaintenanceCampaign = v.object({
  id: v.string(),
  title: v.pipe(v.string(), v.minLength(1)),
  /** What changes and why, in the words that go in every pull request body. */
  description: v.string(),
  kind: MaintenanceKind,
  /** The versioned skill that performs it. Editing the campaign's behaviour is editing that. */
  skill: v.string(),
  state: MaintenanceState,
  /**
   * Diffs allowed to land per run.
   *
   * The single most important number here. It is the size of Monday's review
   * queue, chosen deliberately rather than discovered.
   */
  diffCap: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(500)),
  /** Five-field cron, UTC. Defaults to the quiet window. */
  cron: v.optional(v.string(), '0 3 * * 0'),
  createdBy: v.nullable(v.string()),
  createdAt: v.string(),
  updatedAt: v.string(),
});
export type MaintenanceCampaign = v.InferOutput<typeof MaintenanceCampaign>;

/**
 * One repository's participation in one campaign.
 *
 * Enrollment is per repository and per campaign rather than a global opt-in,
 * because "we take dependency bumps automatically but not framework migrations"
 * is the normal position and a single switch cannot express it.
 */
export const MaintenanceTargetState = v.picklist([
  'pending',
  'in_progress',
  'landed',
  'failed',
  'skipped',
]);
export type MaintenanceTargetState = v.InferOutput<typeof MaintenanceTargetState>;

export const MaintenanceTarget = v.object({
  id: v.string(),
  campaignId: v.string(),
  repo: v.string(),
  /** The team that enrolled it, and the team the failure goes back to. */
  team: v.nullable(v.string()),
  state: MaintenanceTargetState,
  pullRequest: v.optional(v.nullable(v.number()), null),
  attempts: v.optional(v.pipe(v.number(), v.integer()), 0),
  /** The last failure, kept verbatim: it is the evidence the skill is improved from. */
  lastError: v.optional(v.nullable(v.string()), null),
  updatedAt: v.string(),
});
export type MaintenanceTarget = v.InferOutput<typeof MaintenanceTarget>;

/**
 * What a run of a campaign is allowed to do, computed at the moment it starts.
 *
 * Returned rather than enforced silently. A campaign that had sixty eligible
 * repositories and a cap of twenty must tell the agent it was given twenty of
 * sixty — a run that reports "migrated everything eligible" after being handed
 * a third of the list is the fan-out truncation bug with a longer fuse.
 */
export const MaintenanceBatch = v.object({
  campaignId: v.string(),
  /** Targets handed to this run, in enrollment order. */
  targets: v.array(MaintenanceTarget),
  /** Eligible targets that did not fit under the cap. */
  deferred: v.pipe(v.number(), v.integer()),
  cap: v.pipe(v.number(), v.integer()),
  note: v.string(),
});
export type MaintenanceBatch = v.InferOutput<typeof MaintenanceBatch>;

export const MaintenanceReport = v.object({
  campaignId: v.string(),
  title: v.string(),
  state: MaintenanceState,
  enrolled: v.pipe(v.number(), v.integer()),
  landed: v.pipe(v.number(), v.integer()),
  failed: v.pipe(v.number(), v.integer()),
  pending: v.pipe(v.number(), v.integer()),
  skipped: v.pipe(v.number(), v.integer()),
  /** Distinct failure messages, most common first — the input to the next skill version. */
  failureModes: v.optional(v.array(v.object({ reason: v.string(), count: v.number() })), []),
});
export type MaintenanceReport = v.InferOutput<typeof MaintenanceReport>;

/**
 * The quiet window: 03:00 UTC on Sunday.
 *
 * Chosen for when shared CI is idle rather than for when the agent is free. The
 * agent is always free.
 */
export const MAINTENANCE_WINDOW_CRON = '0 3 * * 0';

/** Diffs per run when a campaign does not say. Twenty is a morning's reviewing. */
export const DEFAULT_DIFF_CAP = 20;
